import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container",
    "questionCard",
    "form",
    "star",
    "ratingInput",
    "ratingLabel",
    "submitButton",
    "submitText",
    "feedback",
    "keyboardModal",
    "starsContainer"
  ]

  static values = {
    participationId: Number,
    selectedRating: { type: Number, default: 0 }
  }

  connect() {
    this.preventBackNavigation()
    this.initializeKeyboardNavigation()
    this.updateStarsFromInput()
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
  }

  preventBackNavigation() {
    history.replaceState(null, "", window.location.href)

    window.addEventListener("popstate", () => {
      history.pushState(null, "", window.location.href)
    })
  }

  initializeKeyboardNavigation() {
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    if (this.isModalOpen()) {
      if (event.key === "Escape") {
        this.hideKeyboardHelp()
      }
      return
    }

    const key = event.key

    if (key >= "1" && key <= "5") {
      event.preventDefault()
      this.selectRatingByNumber(parseInt(key))
      return
    }

    if (key === "ArrowLeft") {
      event.preventDefault()
      this.navigateStars(-1)
      return
    }

    if (key === "ArrowRight") {
      event.preventDefault()
      this.navigateStars(1)
      return
    }

    if (key === "Enter" && this.selectedRatingValue > 0) {
      event.preventDefault()
      this.submitForm()
    }
  }

  selectRating(event) {
    const rating = parseInt(event.currentTarget.dataset.rating)
    this.setRating(rating)
  }

  selectRatingByNumber(rating) {
    this.setRating(rating)
    this.focusStar(rating - 1)
  }

  setRating(rating) {
    this.selectedRatingValue = rating

    if (this.hasRatingInputTarget) {
      this.ratingInputTarget.value = rating
    }

    this.updateStarsVisual(rating)
    this.updateRatingLabel(rating)
    this.enableSubmitButton()
    this.showFeedback(rating)
  }

  updateStarsFromInput() {
    if (this.hasRatingInputTarget) {
      const currentRating = parseInt(this.ratingInputTarget.value) || 0
      if (currentRating > 0) {
        this.selectedRatingValue = currentRating
        this.updateStarsVisual(currentRating)
        this.enableSubmitButton()
      }
    }
  }

  updateStarsVisual(rating) {
    this.starTargets.forEach((star, index) => {
      const svg = star.querySelector("svg")
      const isSelected = index < rating

      svg.classList.toggle("text-yellow-400", isSelected)
      svg.classList.toggle("text-gray-300", !isSelected)

      star.setAttribute("aria-checked", index + 1 === rating ? "true" : "false")
    })
  }

  updateRatingLabel(rating) {
    if (!this.hasRatingLabelTarget) return

    const labels = {
      1: "1 - Insuffisant",
      2: "2 - À améliorer",
      3: "3 - Correct",
      4: "4 - Bien",
      5: "5 - Excellent"
    }

    this.ratingLabelTarget.textContent = labels[rating] || ""
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  showFeedback(rating) {
    if (!this.hasFeedbackTarget) return

    this.feedbackTarget.innerHTML = `
      <div class="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm animate-pulse">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        Note sélectionnée : ${rating}/5
      </div>
    `

    setTimeout(() => {
      if (this.hasFeedbackTarget) {
        this.feedbackTarget.innerHTML = ""
      }
    }, 2000)
  }

  navigateStars(direction) {
    const currentIndex = this.selectedRatingValue > 0 ? this.selectedRatingValue - 1 : -1
    let newIndex = currentIndex + direction

    if (newIndex < 0) newIndex = 4
    if (newIndex > 4) newIndex = 0

    this.setRating(newIndex + 1)
    this.focusStar(newIndex)
  }

  focusStar(index) {
    if (this.starTargets[index]) {
      this.starTargets[index].focus()
    }
  }

  submitAnswer(event) {
    if (this.selectedRatingValue === 0) {
      event.preventDefault()
      this.shakeStars()
      return
    }

    this.disableSubmitButton()
    this.showLoadingState()
    this.animateSlideOut()
  }

  submitForm() {
    if (this.hasFormTarget && this.selectedRatingValue > 0) {
      this.disableSubmitButton()
      this.showLoadingState()
      this.animateSlideOut()
      this.formTarget.requestSubmit()
    }
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

  showLoadingState() {
    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.innerHTML = `
        <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `
    }
  }

  animateSlideOut() {
    if (this.hasQuestionCardTarget) {
      this.questionCardTarget.classList.add(
        "transform",
        "transition-all",
        "duration-300",
        "-translate-x-full",
        "opacity-0"
      )
    }
  }

  shakeStars() {
    if (this.hasStarsContainerTarget) {
      this.starsContainerTarget.classList.add("animate-shake")
      setTimeout(() => {
        this.starsContainerTarget.classList.remove("animate-shake")
      }, 500)
    }
  }

  showKeyboardHelp() {
    if (this.hasKeyboardModalTarget) {
      this.keyboardModalTarget.classList.remove("hidden")
      this.keyboardModalTarget.classList.add("flex")
    }
  }

  hideKeyboardHelp() {
    if (this.hasKeyboardModalTarget) {
      this.keyboardModalTarget.classList.add("hidden")
      this.keyboardModalTarget.classList.remove("flex")
    }
  }

  isModalOpen() {
    return this.hasKeyboardModalTarget && !this.keyboardModalTarget.classList.contains("hidden")
  }
}
